import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Loader2,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import useIdentityVerification from '../../hooks/useIdentityVerification';
import { isIdentityAccountBlocked } from '../../utils/identityStatus';

const IDENTITY_OPEN_EVENT = 'identity-verification:open';
const MOBILE_MODAL_BUTTON_CLASS = 'min-h-11 w-full touch-manipulation sm:w-auto';

function formatDobInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (digits.length <= 2) return day;
  if (digits.length <= 4) return `${day}-${month}`;
  return `${day}-${month}-${year}`;
}

export default function IdentityVerificationOnboardingModal({ user }) {
  const [dismissed, setDismissed] = useState(false);
  const [dobInput, setDobInput] = useState('');

  const enabled = Boolean(user) && !isIdentityAccountBlocked(user);
  const guardianManaged = user?.guardian_managed_account === true;
  const {
    phase,
    loading,
    savingDob,
    starting,
    checking,
    error,
    clearError,
    saveDateOfBirth,
    startVerification,
    checkStatus,
  } = useIdentityVerification({
    enabled,
    userId: user?.id,
  });

  useEffect(() => {
    setDismissed(false);
    setDobInput('');
  }, [user?.id]);

  useEffect(() => {
    const handleOpenRequest = () => {
      setDismissed(false);
    };

    window.addEventListener(IDENTITY_OPEN_EVENT, handleOpenRequest);
    return () => {
      window.removeEventListener(IDENTITY_OPEN_EVENT, handleOpenRequest);
    };
  }, []);

  const shouldOfferPopup = useMemo(
    () => [
      'missing_dob',
      'available',
      'pending',
      'failed',
      'guardian_required',
    ].includes(phase),
    [phase],
  );

  const open = enabled && !dismissed && !loading && shouldOfferPopup;

  const saveDob = async () => {
    try {
      await saveDateOfBirth(dobInput);
      setDobInput('');
    } catch {
      // Safe error is rendered below.
    }
  };

  const beginVerification = async () => {
    try {
      await startVerification();
    } catch {
      // Safe error is rendered below.
    }
  };

  const refreshStatus = async () => {
    try {
      await checkStatus();
    } catch {
      // Safe error is rendered below.
    }
  };

  let icon = <ShieldCheck className="h-7 w-7 text-[#002FA7]" />;
  let title = guardianManaged
    ? 'Verify the parent or legal guardian'
    : 'Verify your identity';
  let description = guardianManaged
    ? 'The adult account holder completes this secure Didit check. The young person is not asked to complete an adult identity check, and their profile remains separate from the adult’s private verification details.'
    : 'To help keep Casting Calls UK safe and trusted, we ask eligible users to complete a secure identity check. The verification is completed securely through our verification partner, Didit. Your identity documents and biometric information are processed by Didit and are not stored by Casting Calls UK. The process usually takes only a few minutes.';

  if (phase === 'missing_dob') {
    icon = <CalendarDays className="h-7 w-7 text-[#002FA7]" />;
    title = guardianManaged
      ? 'Complete the adult account holder’s age check'
      : 'Complete your age verification';
    description = guardianManaged
      ? 'Add the parent or legal guardian’s date of birth privately so we can confirm the adult is eligible for the secure verification process.'
      : 'Add your date of birth privately so we can confirm your age and route the correct identity verification process.';
  } else if (phase === 'pending') {
    icon = <Clock3 className="h-7 w-7 text-amber-500" />;
    title = guardianManaged
      ? 'Parent or guardian verification in progress'
      : 'Verification in progress';
    description =
      "Your verification has been submitted and is currently being reviewed. We'll update your account as soon as the result is available.";
  } else if (phase === 'failed') {
    icon = <AlertTriangle className="h-7 w-7 text-red-500" />;
    title = guardianManaged
      ? "We couldn't complete the parent or guardian verification"
      : "We couldn't complete your verification";
    description =
      'Your identity verification could not be completed. Please check the information provided and try again.';
  } else if (phase === 'guardian_required') {
    icon = <Users className="h-7 w-7 text-amber-500" />;
    title = 'Parent or guardian verification required';
    description =
      'This account cannot use the adult identity check. A separate parent or guardian process is required.';
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setDismissed(true);
      }}
    >
      <DialogContent
        className="max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-[calc(100vw-1rem)] max-w-md overflow-y-auto overscroll-contain rounded-2xl border-theme bg-theme-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-theme-text sm:w-full sm:p-6"
        data-testid="identity-onboarding-modal"
      >
        <DialogHeader className="pt-2 sm:pt-0">
          <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full border border-[#002FA7]/30 bg-[#002FA7]/10 sm:mb-2 sm:h-14 sm:w-14">
            {icon}
          </div>
          <DialogTitle className="break-words text-center text-xl leading-tight text-theme-text sm:text-2xl">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed text-theme-muted">
            {description}
          </DialogDescription>
        </DialogHeader>

        {phase === 'missing_dob' && (
          <div className="space-y-3">
            <Label htmlFor="identity-onboarding-dob" className="text-theme-text">
              {guardianManaged ? 'Parent or legal guardian date of birth' : 'Date of birth'}
            </Label>
            <Input
              id="identity-onboarding-dob"
              type="text"
              required
              inputMode="numeric"
              autoComplete="bday"
              enterKeyHint="done"
              maxLength={10}
              pattern="[0-9]{2}-[0-9]{2}-[0-9]{4}"
              placeholder="DD-MM-YYYY"
              value={dobInput}
              onChange={(event) => {
                clearError();
                setDobInput(formatDobInput(event.target.value));
              }}
              className="h-12 border-theme bg-theme/50 text-base text-theme-text sm:h-10 sm:text-sm"
              data-testid="identity-onboarding-dob"
            />
            <p className="text-xs leading-relaxed text-theme-muted">
              Used privately for age and identity verification. It is never
              displayed publicly.
            </p>
          </div>
        )}

        {phase === 'available' && (
          <div className="rounded-lg border border-[#002FA7]/30 bg-[#002FA7]/10 p-4 text-sm leading-relaxed text-theme-muted">
            {guardianManaged
              ? 'The adult account holder’s date of birth is recorded. The next step is their secure Didit identity check.'
              : 'Your date of birth is recorded. The next step is the secure Didit identity check.'}
          </div>
        )}

        {phase === 'available' && (
          <p className="text-center text-xs leading-relaxed text-theme-muted">
            {guardianManaged
              ? 'The parent or legal guardian may be asked to provide valid photo ID and complete a short face check.'
              : 'You may be asked to provide a valid photo ID and complete a short face check.'}
          </p>
        )}

        {phase === 'pending' && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm leading-relaxed text-theme-muted">
            Do not start another verification while this result is being checked.
          </div>
        )}

        {phase === 'failed' && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm leading-relaxed text-theme-muted">
            Retrying creates a new journey only when the backend confirms it is safe.
          </div>
        )}

        {phase === 'guardian_required' && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm leading-relaxed text-theme-muted">
            The adult Didit journey is blocked for users aged 15–17.
          </div>
        )}

        {error && (
          <p className="break-words text-sm leading-relaxed text-red-400" data-testid="identity-onboarding-error">
            {error}
          </p>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
          {phase === 'missing_dob' && (
            <Button
              type="button"
              className={`${MOBILE_MODAL_BUTTON_CLASS} bg-[#002FA7] text-white hover:bg-[#0055FF]`}
              onClick={saveDob}
              disabled={
                savingDob ||
                !/^[0-9]{2}-[0-9]{2}-[0-9]{4}$/.test(dobInput.trim())
              }
              data-testid="identity-onboarding-save-dob"
            >
              {savingDob ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save and continue'
              )}
            </Button>
          )}

          {phase === 'available' && (
            <Button
              type="button"
              className={`${MOBILE_MODAL_BUTTON_CLASS} bg-[#002FA7] text-white hover:bg-[#0055FF]`}
              onClick={beginVerification}
              disabled={starting}
              data-testid="identity-onboarding-start"
            >
              {starting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                guardianManaged ? 'Verify parent or guardian' : 'Start verification'
              )}
            </Button>
          )}

          {phase === 'pending' && (
            <Button
              type="button"
              className={`${MOBILE_MODAL_BUTTON_CLASS} bg-[#002FA7] text-white hover:bg-[#0055FF]`}
              onClick={refreshStatus}
              disabled={checking}
              data-testid="identity-onboarding-check-status"
            >
              {checking ? 'Checking...' : 'Check status'}
            </Button>
          )}

          {phase === 'failed' && (
            <Button
              type="button"
              className={`${MOBILE_MODAL_BUTTON_CLASS} bg-[#002FA7] text-white hover:bg-[#0055FF]`}
              onClick={beginVerification}
              disabled={starting}
              data-testid="identity-onboarding-retry"
            >
              {starting ? 'Opening...' : 'Try again'}
            </Button>
          )}

          {phase === 'guardian_required' && (
            <Button
              type="button"
              variant="outline"
              className={MOBILE_MODAL_BUTTON_CLASS}
              onClick={() => setDismissed(true)}
              data-testid="identity-onboarding-guardian-close"
            >
              Understood
            </Button>
          )}

          {phase !== 'guardian_required' && (
            <Button
              type="button"
              variant="outline"
              className={MOBILE_MODAL_BUTTON_CLASS}
              onClick={() => setDismissed(true)}
              data-testid="identity-onboarding-later"
            >
              Not now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
